<template>
  <div class="quill-editor-container">
    <div ref="editorRef" class="quill-editor"></div>
  </div>
</template>

<script>
export default {
  name: 'QuillEditor',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Type your message...'
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: '120px'
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      quill: null,
      isUpdating: false
    };
  },
  mounted() {
    this.loadQuill();
  },
  beforeUnmount() {
    if (this.quill) {
      this.quill.off('text-change', this.onTextChange);
      this.quill = null;
    }
  },
  watch: {
    modelValue(newValue) {
      if (!this.isUpdating && this.quill) {
        const currentContent = this.quill.root.innerHTML;
        if (newValue !== currentContent) {
          this.quill.root.innerHTML = newValue;
        }
      }
    }
  },
  methods: {
    loadQuill() {
      // Check if Quill is already loaded globally
      if (window.Quill) {
        this.initQuill(window.Quill);
        return;
      }

      // Load Quill from CDN if not available
      const script = document.createElement('script');
      script.src = 'https://cdn.quilljs.com/1.3.6/quill.min.js';
      script.onload = () => {
        // Load CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.quilljs.com/1.3.6/quill.snow.css';
        document.head.appendChild(link);
        
        this.initQuill(window.Quill);
      };
      document.head.appendChild(script);
    },
    
    initQuill(Quill) {
      const options = {
        theme: 'snow',
        placeholder: this.placeholder,
        readOnly: this.readOnly,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'script': 'sub'}, { 'script': 'super' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],
            ['link', 'image']
          ]
        }
      };

      this.quill = new Quill(this.$refs.editorRef, options);
      
      // Set initial content
      if (this.modelValue) {
        this.quill.root.innerHTML = this.modelValue;
      }

      // Listen for text changes
      this.quill.on('text-change', this.onTextChange);
      
      // Set height
      this.$refs.editorRef.style.height = this.height;
    },
    
    onTextChange() {
      if (!this.isUpdating) {
        this.isUpdating = true;
        const content = this.quill.root.innerHTML;
        this.$emit('update:modelValue', content);
        this.$emit('change', content);
        this.$nextTick(() => {
          this.isUpdating = false;
        });
      }
    },
    
    getText() {
      return this.quill ? this.quill.getText() : '';
    },
    
    getHTML() {
      return this.quill ? this.quill.root.innerHTML : '';
    },
    
    setContent(content) {
      if (this.quill) {
        this.quill.root.innerHTML = content;
      }
    },
    
    clear() {
      if (this.quill) {
        this.quill.setText('');
      }
    },
    
    focus() {
      if (this.quill) {
        this.quill.focus();
      }
    }
  }
};
</script>

<style scoped>
.quill-editor-container {
  width: 100%;
}

.quill-editor {
  width: 100%;
}

:deep(.ql-editor) {
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.5;
}

:deep(.ql-toolbar) {
  border-top: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  background: #f9f9f9;
}

:deep(.ql-container) {
  border-bottom: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 6px 6px;
}

:deep(.ql-editor:focus) {
  border-color: #245B5F;
  box-shadow: 0 0 0 2px rgba(36, 91, 95, 0.1);
}

:deep(.ql-toolbar button:hover),
:deep(.ql-toolbar button:focus) {
  color: #245B5F;
}

:deep(.ql-toolbar button:hover .ql-stroke),
:deep(.ql-toolbar button:focus .ql-stroke) {
  stroke: #245B5F;
}

:deep(.ql-toolbar button:hover .ql-fill),
:deep(.ql-toolbar button:focus .ql-fill) {
  fill: #245B5F;
}

:deep(.ql-toolbar button.ql-active) {
  color: #245B5F;
}

:deep(.ql-toolbar button.ql-active .ql-stroke) {
  stroke: #245B5F;
}

:deep(.ql-toolbar button.ql-active .ql-fill) {
  fill: #245B5F;
}
</style> 